import { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/store/authStore';
import {
  UserGroupIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';

interface UserWithPerms {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_admin: boolean;
  crm_permission?: string;
  crm_can_view_revenue?: boolean;
  crm_can_manage_pipeline?: boolean;
  crm_can_import_contacts?: boolean;
  crm_can_delete_contacts?: boolean;
}

const permLevels = ['admin', 'commercial', 'support', 'readonly'];
const permColors: Record<string, string> = {
  admin: 'bg-red-100 text-red-700',
  commercial: 'bg-blue-100 text-blue-700',
  support: 'bg-green-100 text-green-700',
  readonly: 'bg-gray-100 text-gray-700',
};

export function SettingsPage() {
  const { user } = useAuthStore();
  const [users, setUsers] = useState<UserWithPerms[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'crm' | 'sla' | 'general'>('crm');

  // SLA state
  interface SLAPolicy {
    id: string;
    priority: string;
    triage_minutes: number;
    allocation_minutes: number;
    resolution_minutes: number;
    uses_working_hours: boolean;
  }
  const [slaPolicies, setSlaPolicies] = useState<SLAPolicy[]>([]);
  const [slaLoading, setSlaLoading] = useState(false);
  const [slaSaving, setSlaSaving] = useState<string | null>(null);
  const [slaEdits, setSlaEdits] = useState<Record<string, SLAPolicy>>({});

  const fetchSlaPolicies = async () => {
    setSlaLoading(true);
    try {
      const res = await apiClient.get('/sla/policies');
      const policies: SLAPolicy[] = res.data || [];
      setSlaPolicies(policies);
      // Seed edit state
      const edits: Record<string, SLAPolicy> = {};
      policies.forEach((p) => { edits[p.priority] = { ...p }; });
      setSlaEdits(edits);
    } catch (e) {
      console.error('Failed to load SLA policies', e);
    } finally {
      setSlaLoading(false);
    }
  };

  const saveSlaPolicy = async (priority: string) => {
    const edited = slaEdits[priority];
    if (!edited) return;
    setSlaSaving(priority);
    try {
      await apiClient.put(`/sla/policies/${priority}`, {
        triage_minutes: edited.triage_minutes,
        allocation_minutes: edited.allocation_minutes,
        resolution_minutes: edited.resolution_minutes,
        uses_working_hours: edited.uses_working_hours,
      });
      toast.success(`${priority.charAt(0).toUpperCase() + priority.slice(1)} SLA saved`);
      await fetchSlaPolicies();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to save SLA policy');
    } finally {
      setSlaSaving(null);
    }
  };

  const updateSlaField = (priority: string, field: keyof SLAPolicy, value: any) => {
    setSlaEdits((prev) => ({
      ...prev,
      [priority]: { ...prev[priority], [field]: value },
    }));
  };

  const minutesToDisplay = (mins: number): string => {
    if (mins === 0) return 'Immediate (30 min)';
    if (mins < 60) return `${mins} min`;
    if (mins < 1440) return `${mins / 60} hour${mins / 60 !== 1 ? 's' : ''}`;
    if (mins < 10080) return `${mins / 1440} day${mins / 1440 !== 1 ? 's' : ''}`;
    return `${Math.round(mins / 1440)} days`;
  };

  const isAdmin = user?.is_admin || user?.isAdmin || (user as any)?.role === "admin";

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersRes = await apiClient.get('/users');
      const usersList = usersRes.data?.data || usersRes.data || [];

      // Fetch all CRM permissions in one call
      const permsRes = await apiClient.get('/contacts/permissions/list');
      const perms = permsRes.data || {};

      const enriched = usersList.map((u: any) => {
        const p = perms[u.id];
        return {
          ...u,
          crm_permission: p ? p.permission_level : null,
          crm_can_view_revenue: p?.can_view_revenue,
          crm_can_manage_pipeline: p?.can_manage_pipeline,
          crm_can_import_contacts: p?.can_import_contacts,
          crm_can_delete_contacts: p?.can_delete_contacts,
        };
      });
      setUsers(enriched);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); fetchSlaPolicies(); }, []);

  const updateCRMAccess = async (userId: string, level: string | null) => {
    setSaving(userId);
    try {
      if (level) {
        await apiClient.post('/contacts/permissions', {
          user_id: userId,
          permission_level: level,
          can_view_revenue: level === 'admin' || level === 'commercial',
          can_manage_pipeline: level === 'admin' || level === 'commercial',
          can_import_contacts: level !== 'readonly',
          can_delete_contacts: level === 'admin',
          can_view_call_recordings: level === 'admin' || level === 'commercial',
          can_export_data: level !== 'readonly',
        });
      } else {
        await apiClient.delete(`/contacts/permissions/${userId}`);
      }
      fetchUsers();
    } catch (err) {
      console.error('Failed to update permissions:', err);
    } finally {
      setSaving(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-2 text-lg font-medium text-gray-900">Admin Access Required</h2>
        <p className="text-sm text-gray-500">Only admins can manage settings.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Settings</h1>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {[
            { id: 'crm', label: 'CRM Access', icon: UserGroupIcon },
            { id: 'sla', label: 'SLA Settings', icon: ClockIcon },
            { id: 'general', label: 'General', icon: Cog6ToothIcon },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={cn(
                'flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* CRM Access Tab */}
      {activeTab === 'crm' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">CRM Permissions</h2>
            <p className="text-sm text-gray-500 mt-1">
              Control who can access the CRM module and what they can do.
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 font-medium text-gray-500">User</th>
                <th className="text-left px-6 py-3 font-medium text-gray-500">CRM Access</th>
                <th className="text-left px-6 py-3 font-medium text-gray-500">Permissions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={3} className="px-6 py-8 text-center text-gray-400">Loading...</td></tr>
              ) : (
                users
                  .filter((u) => !u.email.includes('kestrel') && !u.email.includes('pippin'))
                  .map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3">
                        <p className="font-medium text-gray-900">{u.first_name} {u.last_name}</p>
                        <p className="text-xs text-gray-500">{u.email}</p>
                      </td>
                      <td className="px-6 py-3">
                        <select
                          value={u.crm_permission || ''}
                          onChange={(e) => updateCRMAccess(u.id, e.target.value || null)}
                          disabled={saving === u.id}
                          className={cn(
                            'text-sm border rounded-lg px-3 py-1.5 bg-white',
                            saving === u.id ? 'opacity-50' : '',
                            u.crm_permission ? 'border-primary-300' : 'border-gray-300'
                          )}
                        >
                          <option value="">No Access</option>
                          {permLevels.map((p) => (
                            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-3">
                        {u.crm_permission ? (
                          <div className="flex flex-wrap gap-1">
                            {u.crm_can_view_revenue && <span className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-600">Revenue</span>}
                            {u.crm_can_manage_pipeline && <span className="px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-600">Pipeline</span>}
                            {u.crm_can_import_contacts && <span className="px-2 py-0.5 rounded text-xs bg-green-50 text-green-600">Import</span>}
                            {u.crm_can_delete_contacts && <span className="px-2 py-0.5 rounded text-xs bg-red-50 text-red-600">Delete</span>}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* SLA Settings Tab */}
      {activeTab === 'sla' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">SLA Policy Settings</h2>
            <p className="text-sm text-gray-500 mt-1">
              Define response time targets per ticket priority. Changes apply to new tickets only.
              <span className="ml-2 text-amber-600 font-medium">⚠ Immediate = 30-min grace period before amber/red.</span>
            </p>
          </div>

          {slaLoading ? (
            <div className="p-8 text-center text-gray-400">Loading SLA policies...</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {slaPolicies.map((policy) => {
                const edit = slaEdits[policy.priority] || policy;
                const priorityColors: Record<string, string> = {
                  critical: 'bg-red-50 border-l-4 border-red-500',
                  high:     'bg-orange-50 border-l-4 border-orange-400',
                  medium:   'bg-amber-50 border-l-4 border-amber-400',
                  low:      'bg-green-50 border-l-4 border-green-400',
                };
                const badgeColors: Record<string, string> = {
                  critical: 'bg-red-100 text-red-700',
                  high:     'bg-orange-100 text-orange-700',
                  medium:   'bg-amber-100 text-amber-700',
                  low:      'bg-green-100 text-green-700',
                };
                return (
                  <div key={policy.priority} className={`p-6 ${priorityColors[policy.priority] || ''}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold capitalize ${badgeColors[policy.priority] || 'bg-gray-100 text-gray-700'}`}>
                          {policy.priority}
                        </span>
                        <span className="text-xs text-gray-500">
                          Current: Triage {minutesToDisplay(policy.triage_minutes)} · Allocation {minutesToDisplay(policy.allocation_minutes)} · Resolution {minutesToDisplay(policy.resolution_minutes)}
                        </span>
                      </div>
                      <button
                        onClick={() => saveSlaPolicy(policy.priority)}
                        disabled={slaSaving === policy.priority}
                        className="px-4 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
                      >
                        {slaSaving === policy.priority ? 'Saving...' : 'Save'}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Triage */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Triage (minutes)</label>
                        <input
                          type="number"
                          min={0}
                          value={edit.triage_minutes}
                          onChange={(e) => updateSlaField(policy.priority, 'triage_minutes', parseInt(e.target.value) || 0)}
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-primary-500 focus:border-primary-500 bg-white"
                        />
                        <p className="text-xs text-gray-400 mt-1">{minutesToDisplay(edit.triage_minutes)}</p>
                      </div>

                      {/* Allocation */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Allocation (minutes)</label>
                        <input
                          type="number"
                          min={0}
                          value={edit.allocation_minutes}
                          onChange={(e) => updateSlaField(policy.priority, 'allocation_minutes', parseInt(e.target.value) || 0)}
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-primary-500 focus:border-primary-500 bg-white"
                        />
                        <p className="text-xs text-gray-400 mt-1">{minutesToDisplay(edit.allocation_minutes)}</p>
                      </div>

                      {/* Resolution */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Resolution (minutes)</label>
                        <input
                          type="number"
                          min={1}
                          value={edit.resolution_minutes}
                          onChange={(e) => updateSlaField(policy.priority, 'resolution_minutes', parseInt(e.target.value) || 1)}
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-primary-500 focus:border-primary-500 bg-white"
                        />
                        <p className="text-xs text-gray-400 mt-1">{minutesToDisplay(edit.resolution_minutes)}</p>
                      </div>

                      {/* Working hours */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Clock type</label>
                        <select
                          value={edit.uses_working_hours ? 'working' : 'calendar'}
                          onChange={(e) => updateSlaField(policy.priority, 'uses_working_hours', e.target.value === 'working')}
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-primary-500 focus:border-primary-500 bg-white"
                        >
                          <option value="calendar">24/7 Calendar</option>
                          <option value="working">Working hours (Mon-Fri 09-17)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="p-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
            <p className="text-xs text-gray-400">
              💡 Enter <strong>0</strong> for Triage/Allocation to mean "Immediate" (30-min grace). 
              Resolution minimum is 1 minute. Working hours = Mon-Fri 09:00–17:00 UTC.
            </p>
          </div>
        </div>
      )}

      {/* General Tab */}
      {activeTab === 'general' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Cog6ToothIcon className="mx-auto h-12 w-12 text-gray-300" />
          <p className="text-gray-500 mt-3">General settings coming soon</p>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
